/**
 * axisPointer 组件扩展
 *
 * @see https://echarts.apache.org/zh/option.html#axisPointer
 */
import type ExtensionAPI from 'echarts/types/src/core/ExtensionAPI';
import type GlobalModel from 'echarts/types/src/model/Global';
import type GridModel from 'echarts/types/src/coord/cartesian/GridModel';
import type { EChartsExtensionInstallRegisters } from 'echarts';
import type { Element, ElementProps } from 'zrender';

export function install(registers: EChartsExtensionInstallRegisters) {
  //

  // eslint-disable-next-line sonarjs/cognitive-complexity
  registers.registerUpdateLifecycle('afterinit', echartsIns => {
    /**
     * * 没有渲染任何数据内容时，不显示 axisPointer
     *
     * ! 当前的实现仅考虑了利用配置项进行控制的简单场景，实际上还应该判断 axisPointer 与哪几个系列相关联
     */
    echartsIns.on('updateAxisPointer', event => {
      const axisPointerOptions: { dvShowOnlyWhenRenderingContent?: boolean }[] =
        echartsIns.getOption().axisPointer || [];

      if (
        event.axesInfo.length <= 0 ||
        !axisPointerOptions.some(opt => opt.dvShowOnlyWhenRenderingContent === true)
      ) {
        return;
      }

      const allSeriesNotAlive = (echartsIns._chartsViews as { __alive: boolean }[]).every(
        seriesView => seriesView.__alive === false
      );

      if (allSeriesNotAlive) {
        // 隐藏
        echartsIns.dispatchAction({ type: 'updateAxisPointer' });
      }

      //
    });

    /**
     * * 表内触发 axisPointer 后，手指移出图表外依然触发 axisPointer
     */
    let mousedownFlag = false;
    echartsIns.getZr().on('mousedown', event => {
      const triggerCompModel = (echartsIns._api as ExtensionAPI).getComponentByElement(
        event.target as unknown as Element<ElementProps>
      );
      if (triggerCompModel?.mainType === 'dataZoom') {
        return;
      }

      mousedownFlag = true;
    });

    echartsIns.getZr().on('mouseup', () => {
      mousedownFlag = false;
    });

    echartsIns.getZr().on('mousemove', event => {
      if (!mousedownFlag) {
        return;
      }

      if (event.$dvGetInteractionType() !== '') {
        return;
      }

      const triggerCompModel = (echartsIns._api as ExtensionAPI).getComponentByElement(
        event.target as unknown as Element<ElementProps>
      );
      if (triggerCompModel) {
        return;
      }

      let nearestGridModel: GridModel;

      (echartsIns.getModel() as GlobalModel).eachComponent('grid', (compModel: GridModel) => {
        const rect = compModel.coordinateSystem.getRect();
        // ! 在 grid 内
        if (rect.contain(event.offsetX, event.offsetY)) {
          return;
        }
        if (!nearestGridModel) {
          nearestGridModel = compModel;

          return;
        }

        const rectCenter = [rect.x + rect.width / 2, rect.y + rect.height / 2];
        const lastRect = nearestGridModel.coordinateSystem.getRect();
        const lastRectCenter = [lastRect.x + lastRect.width / 2, lastRect.y + lastRect.height / 2];

        if (
          (event.offsetX - lastRectCenter[0]) ** 2 + (event.offsetY - lastRectCenter[1]) ** 2 >
          (event.offsetX - rectCenter[0]) ** 2 + (event.offsetY - rectCenter[1]) ** 2
        ) {
          nearestGridModel = compModel;
        }
      });
      if (!nearestGridModel) {
        return;
      }

      const rect = nearestGridModel.coordinateSystem.getRect();
      const rectCenter = [rect.x + rect.width / 2, rect.y + rect.height / 2];
      let targetPoint: [number, number] = [null, null];

      if (event.offsetX >= rect.x && event.offsetX <= rect.x + rect.width) {
        targetPoint = [event.offsetX, rectCenter[1]];
      }
      if (event.offsetY >= rect.y && event.offsetY <= rect.y + rect.height) {
        targetPoint = [rectCenter[0], event.offsetY];

        // * 先不处理用户在水平方向上的表外操作
        return;
      }
      if (targetPoint[0] === null) {
        return;
      }

      const PENDING_TO_DELAY = 10;

      // ! 必须使用延迟在 echarts 内部调用 dispatchAction 后再调用
      setTimeout(() => {
        echartsIns.dispatchAction({
          type: 'updateAxisPointer',
          x: targetPoint[0],
          y: targetPoint[1]
        });
      }, PENDING_TO_DELAY);

      //
    });

    //
  });

  //
}
